import React, { useState } from 'react'

import classNames from 'classnames'

import arrow from '../../assets/svg/chevron_right.svg'

import CloseModal from './components/CloseModal'
import AddNewLoaner from './components/AddNewLoaner'
import { ActiveLoanersTable } from './components/ActiveLoanersTable'
import { ClosedLoanersTable } from './components/ClosedLoanersTable'
import ConfirmModal from './components/ConfirmModal'

import { useLoaners } from './hooks/useLoaners'
import { useDevices } from './hooks/useDevices'
import { useSaveOnEnter } from './hooks/useSaveOnEnter'

import styles from './LoanerVehicles.module.scss'

export interface IMinimalDevice {
  id: string
  name: string
}

export interface ILoanerRecord {
  id: string
  assignedVehicle: IMinimalDevice
  loanerVehicle: IMinimalDevice | null
  issuedDate: string
  estimatedReturnDate: string
  assignedStaff: string
  note: string
  closedDate: string
  isEditing: boolean
}

export const TABLE_HEADERS = [
  'Assigned Vehicle',
  'Loaner Vehicle',
  'Issued Date',
  'Estimated Return',
  'Assigned Staff',
  'Note',
  'Closed Date',
  'Action',
]

export const LoanerVehicles = () => {
  const [isTableHidden, setIsTableHidden] = useState({
    activeTable: false,
    closedTable: true,
  })

  const { availableDevices } = useDevices()

  const {
    activeLoaners,
    closedLoaners,
    isLoading,
    confirmModal,
    handleAddNewLoaner,
    handleSave,
    handleEdit,
    handleDelete,
    handleFieldChange,
    handleLoanerVehicleSelect,
    handleReopenLoaner,
    handleDeleteClosed,
    handleConfirmAction,
    handleCloseModal
  } = useLoaners()

  useSaveOnEnter(activeLoaners, handleSave)

  const toggleTable = (tableName: 'activeTable' | 'closedTable') => {
    setIsTableHidden((prev) => ({
      ...prev,
      [tableName]: !prev[tableName],
    }))
  }

  return (
    <section className={styles['loaner-vehicles-wrapper']}>
      <div className={styles['loaner-vehicles']}>
        <h1>Loaner Vehicles</h1>

        {/* Close button */}
        <CloseModal />

        {/* Add button with dropdown */}
        <AddNewLoaner
          availableDevices={availableDevices}
          handleDropdownSelect={handleAddNewLoaner}
        />

        {/* Active loaners */}
        <div
          className={classNames({
            [styles.expandedSection]: !isTableHidden.activeTable,
            [styles.collapsedSection]: isTableHidden.activeTable,
          })}
        >
          <h3
            className={styles.sectionHeader}
            onClick={() => toggleTable('activeTable')}
          >
            Active ({activeLoaners.length})
            <span
              className={classNames(styles.arrow, {
                [styles.arrowRotated]: !isTableHidden.activeTable,
              })}
            >
              <img src={arrow} alt="toggle" />
            </span>
          </h3>

          <ActiveLoanersTable
            loaners={activeLoaners}
            onSave={handleSave}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onFieldChange={handleFieldChange}
            onLoanerVehicleSelect={handleLoanerVehicleSelect}
            availableDevices={availableDevices}
            isTableHidden={isTableHidden.activeTable}
            isLoading={isLoading}
          />
        </div>

        {/* Closed loaners */}
        <div
          className={classNames({
            [styles.expandedSection]: !isTableHidden.closedTable,
            [styles.collapsedSection]: isTableHidden.closedTable,
          })}
        >
          <h3
            className={styles.sectionHeader}
            onClick={() => toggleTable('closedTable')}
          >
            Closed ({closedLoaners.length})
            <span
              className={classNames(styles.arrow, {
                [styles.arrowRotated]: !isTableHidden.closedTable,
              })}
            >
              <img src={arrow} alt="toggle" />
            </span>
          </h3>

          <ClosedLoanersTable
            loaners={closedLoaners}
            onReopen={handleReopenLoaner}
            onDelete={handleDeleteClosed}
            isTableHidden={isTableHidden.closedTable}
            isLoading={isLoading}
          />
        </div>

        <ConfirmModal
          open={confirmModal.open}
          onClose={handleCloseModal}
          onConfirm={handleConfirmAction}
          actionType={confirmModal.action}
        />
      </div>
    </section>
  )
}
